import React, { useEffect, useMemo, useState } from 'react';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import './UserGuidePage.css';

const GUIDE_LANGUAGES = {
  en: { label: 'English', file: '/docs/USER_GUIDE_EN.md', loading: 'Loading user guide…', error: 'The user guide could not be loaded.', back: 'Back to app', guide: 'User Guide', contents: 'Quick contents' },
  fr: { label: 'Français', file: '/docs/USER_GUIDE_FR.md', loading: 'Chargement du guide…', error: 'Impossible de charger le guide.', back: "Retour à l’application", guide: "Guide d’utilisation", contents: 'Sommaire' },
  'zh-CN': { label: '简体中文', file: '/docs/USER_GUIDE_ZH_CN.md', loading: '正在载入用户手册……', error: '无法载入用户手册。', back: '返回应用', guide: '用户手册', contents: '快速目录' },
  'zh-TW': { label: '繁體中文', file: '/docs/USER_GUIDE_ZH_TW.md', loading: '正在載入使用者手冊……', error: '無法載入使用者手冊。', back: '返回應用程式', guide: '使用者手冊', contents: '快速目錄' },
};

const initialLanguage = () => {
  const saved = localStorage.getItem('marstrix-user-guide-language');
  if (GUIDE_LANGUAGES[saved]) return saved;
  const appLanguage = localStorage.getItem('marstrix-language') || localStorage.getItem('language') || '';
  if (/traditional|繁/i.test(appLanguage)) return 'zh-TW';
  if (/simplified|简/i.test(appLanguage)) return 'zh-CN';
  if (/french|français|^fr/i.test(appLanguage)) return 'fr';
  return 'en';
};

const inlineContent = (text) => {
  const parts = String(text || '').split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((part, index) => part.startsWith('http')
    ? <a key={`${part}-${index}`} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
    : part);
};

function MarkdownGuide({ source }) {
  const blocks = useMemo(() => {
    const lines = source.replace(/\r/g, '').split('\n');
    const result = [];
    let list = [];
    let paragraph = [];

    const flushList = () => {
      if (list.length) result.push({ type: 'list', items: list });
      list = [];
    };
    const flushParagraph = () => {
      if (paragraph.length) result.push({ type: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    };

    lines.forEach((rawLine) => {
      const line = rawLine.trim();
      const heading = /^(#{1,3})\s+(.+)$/.exec(line);
      const bullet = /^-\s+(.+)$/.exec(line);
      const numbered = /^\d+\.\s+(.+)$/.exec(line);
      if (!line) { flushParagraph(); flushList(); return; }
      if (heading) {
        flushParagraph(); flushList();
        result.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      } else if (bullet || numbered) {
        flushParagraph();
        list.push((bullet || numbered)[1]);
      } else if (line.startsWith('> ')) {
        flushParagraph(); flushList(); result.push({ type: 'note', text: line.slice(2) });
      } else {
        paragraph.push(line);
      }
    });
    flushParagraph(); flushList();
    return result;
  }, [source]);

  return blocks.map((block, index) => {
    if (block.type === 'heading') {
      const Tag = `h${block.level}`;
      const id = block.text.replace(/^\d+(?:\.\d+)*\.?\s*/, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').toLowerCase();
      return <Tag id={id || `section-${index}`} key={`${block.text}-${index}`}>{block.text}</Tag>;
    }
    if (block.type === 'list') return <ul key={`list-${index}`}>{block.items.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{inlineContent(item)}</li>)}</ul>;
    if (block.type === 'note') return <aside key={`note-${index}`}>{inlineContent(block.text)}</aside>;
    return <p key={`paragraph-${index}`}>{inlineContent(block.text)}</p>;
  });
}

export default function UserGuidePage() {
  const [language, setLanguage] = useState(initialLanguage);
  const [source, setSource] = useState('');
  const [error, setError] = useState('');
  const copy = GUIDE_LANGUAGES[language];

  useEffect(() => {
    setSource('');
    setError('');
    localStorage.setItem('marstrix-user-guide-language', language);
    document.documentElement.lang = language;
    fetch(copy.file)
      .then((response) => {
        if (!response.ok) throw new Error('Guide could not be loaded.');
        return response.text();
      })
      .then(setSource)
      .catch(() => setError(copy.error));
  }, [copy, language]);

  return <main className="user-guide-page">
    <header className="user-guide-header">
      <a href="/protected"><ArrowBackRoundedIcon /> {copy.back}</a>
      <div><span>M</span>MARSTRIX</div>
      <label><span>{copy.guide}</span><select value={language} onChange={(event) => setLanguage(event.target.value)} aria-label="Guide language">{Object.entries(GUIDE_LANGUAGES).map(([key, item]) => <option key={key} value={key}>{item.label}</option>)}</select></label>
    </header>
    <div className="user-guide-layout">
      <nav aria-label="用户手册目录">
        <div><MenuBookRoundedIcon /><b>{copy.contents}</b></div>
        {source.split('\n').filter((line) => line.startsWith('## ')).map((line) => { const title = line.slice(3); const id = title.replace(/^\d+(?:\.\d+)*\.?\s*/, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').toLowerCase(); return <a key={title} href={`#${id}`}>{title.replace(/^\d+\.\s*/, '')}</a>; })}
      </nav>
      <article className="user-guide-document">
        {!source && !error && <p className="user-guide-loading">{copy.loading}</p>}
        {error && <p className="user-guide-error">{error}</p>}
        {source && <MarkdownGuide source={source} />}
      </article>
    </div>
  </main>;
}
